import { useCallback, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  EditableToken,
  useContentToken,
  useProperty,
} from '@vibesites/runtime/react';

// ─── Rent Affordability Calculator ──────────────────────────────────────────
//
// Self-hosted replacement for the third-party affordability widget (which was
// domain-gated on the vendor's backend). A user enters their monthly household
// income and we divide it by the property's income-qualification multiplier to
// show the maximum rent they can afford.
//
// The multiplier comes from the property's
// `custom_household_income_qualification_multiplier` custom field, defaulting
// to 3 when unset or invalid (i.e. income must be 3× the rent).
//
// The dialog is a Radix Dialog: centered overlay, focus trap, Escape/outside
// close, and body-scroll lock (via Radix's RemoveScroll) all handled for us.
// Radix keeps the content mounted through the exit animation, so open AND close
// animate (see <style>). The content is a flex-centered scroll container with a
// `margin:auto` panel — that keeps short dialogs centered while letting tall
// ones scroll fully on mobile without clipping the top.

const DEFAULT_MULTIPLIER = 3;

const usd = (n: number) =>
  n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

// ─── Component ────────────────────────────────────────────────────────────────

export function CheckAffordability() {
  // Defaults live in the contentSchema below; the hook resolves them.
  const label = useContentToken('checkaffordability.label');
  const title = useContentToken('checkaffordability.title');
  const description = useContentToken('checkaffordability.description');
  const note1 = useContentToken('checkaffordability.note1');
  const note2 = useContentToken('checkaffordability.note2');

  // Read the property's income-qualification multiplier, defaulting to 3.
  const property = useProperty();
  const multiplier = useMemo(() => {
    const raw = property['custom_household_income_qualification_multiplier'];
    const n = typeof raw === 'number' ? raw : parseFloat(String(raw ?? ''));
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_MULTIPLIER;
  }, [property]);

  const [open, setOpen] = useState(false);
  const [income, setIncome] = useState(''); // raw digits only
  const [affordable, setAffordable] = useState<number | null>(null);

  // Reset the calculator each time the dialog closes so it reopens fresh.
  const onOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) {
      setIncome('');
      setAffordable(null);
    }
  }, []);

  const displayIncome = income ? Number(income).toLocaleString('en-US') : '';

  const calculate = useCallback(() => {
    const monthly = Number(income);
    if (!monthly || monthly <= 0) {
      setAffordable(null);
      return;
    }
    setAffordable(monthly / multiplier);
  }, [income, multiplier]);

  return (
    <section className="bg-white pt-12 md:pt-16">
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
          <Dialog.Trigger asChild>
            <button
              type="button"
              className="font-body"
              style={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '14px 24px',
                background: 'var(--vibe-color-primary)',
                color: 'var(--vibe-color-text-on-primary, #fff)',
                fontSize: '15px',
                fontWeight: 600,
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                boxSizing: 'border-box',
                transition: 'background .15s',
              }}
            >
              <EditableToken
                path="checkaffordability.label"
                as="span"
                label="Button Label"
              >
                {label}
              </EditableToken>
            </button>
          </Dialog.Trigger>

          <Dialog.Portal>
            <Dialog.Overlay className="alppcalc-overlay" />
            <Dialog.Content
              className="alppcalc-content font-body"
              // Description is rendered visually below; suppress Radix's
              // aria-describedby requirement (we label via the sr-only Title).
              aria-describedby={undefined}
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              {/* Screen-reader label for the dialog. The visible, editable
                  heading is rendered below. */}
              <Dialog.Title className="alppcalc-sr-only">
                {title || 'Rent Affordability Calculator'}
              </Dialog.Title>

              <div className="alppcalc-panel">
                <Dialog.Close
                  className="alppcalc-close"
                  aria-label="Close"
                  type="button"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </Dialog.Close>

                <EditableToken
                  path="checkaffordability.title"
                  as="h2"
                  label="Calculator Title"
                  className="alppcalc-title"
                >
                  {title}
                </EditableToken>

                <EditableToken
                  path="checkaffordability.description"
                  as="p"
                  label="Calculator Description"
                  className="alppcalc-desc"
                >
                  {description}
                </EditableToken>

                <div className="alppcalc-input-wrap">
                  <span className="alppcalc-dollar" aria-hidden="true">
                    $
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    className="alppcalc-input"
                    placeholder="5,000"
                    aria-label="Monthly household income"
                    value={displayIncome}
                    onChange={(e) =>
                      setIncome(e.target.value.replace(/[^\d]/g, ''))
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') calculate();
                    }}
                  />
                </div>

                <button
                  type="button"
                  className="alppcalc-calculate"
                  onClick={calculate}
                >
                  Calculate
                </button>

                {affordable !== null && (
                  <div
                    className="alppcalc-result"
                    role="status"
                    aria-live="polite"
                  >
                    <div className="alppcalc-amount">{usd(affordable)}</div>
                    <div className="alppcalc-result-label">
                      You may qualify up to this amount per month in total rent
                    </div>
                  </div>
                )}

                <div className="alppcalc-note">
                  <span className="alppcalc-note-icon" aria-hidden="true">
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <polyline points="16 11 18 13 22 9" />
                    </svg>
                  </span>
                  <EditableToken
                    path="checkaffordability.note1"
                    as="p"
                    label="Note (verifiable income)"
                    className="alppcalc-note-text"
                  >
                    {note1}
                  </EditableToken>
                </div>

                <div className="alppcalc-note">
                  <span className="alppcalc-note-icon" aria-hidden="true">
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="8" y1="13" x2="16" y2="13" />
                      <line x1="8" y1="17" x2="16" y2="17" />
                    </svg>
                  </span>
                  <EditableToken
                    path="checkaffordability.note2"
                    as="p"
                    label="Note (verifiable sources detail)"
                    className="alppcalc-note-text"
                  >
                    {note2}
                  </EditableToken>
                </div>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </div>

      <style>{`
        .alppcalc-sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }

        .alppcalc-overlay {
          position: fixed;
          inset: 0;
          z-index: 2000;
          background: rgba(0, 0, 0, 0.6);
        }
        .alppcalc-overlay[data-state='open'] {
          animation: alppcalc-overlay-in 200ms ease;
        }
        .alppcalc-overlay[data-state='closed'] {
          animation: alppcalc-overlay-out 160ms ease;
        }
        @keyframes alppcalc-overlay-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes alppcalc-overlay-out {
          from { opacity: 1; }
          to { opacity: 0; }
        }

        /* Full-viewport, flex-centered scroll container. Using dvh + a
           margin:auto child keeps short dialogs centered while tall ones scroll
           fully (no clipped top on mobile). overscroll-behavior:contain stops
           scroll chaining to the page behind. */
        .alppcalc-content {
          position: fixed;
          inset: 0;
          z-index: 2001;
          display: flex;
          padding: 1.25rem;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
        }

        .alppcalc-panel {
          position: relative;
          margin: auto;
          width: min(100%, 640px);
          background: #fff;
          border-radius: 16px;
          padding: 2.25rem;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
          transform-origin: center;
        }
        .alppcalc-content[data-state='open'] .alppcalc-panel {
          animation: alppcalc-in 220ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .alppcalc-content[data-state='closed'] .alppcalc-panel {
          animation: alppcalc-out 160ms ease-in;
        }
        @keyframes alppcalc-in {
          from { opacity: 0; transform: scale(0.96) translateY(8px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes alppcalc-out {
          from { opacity: 1; transform: scale(1) translateY(0); }
          to { opacity: 0; transform: scale(0.96) translateY(8px); }
        }

        .alppcalc-close {
          position: absolute;
          top: 1.25rem;
          right: 1.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: none;
          background: #f2f2f4;
          color: #1a1a1a;
          cursor: pointer;
          transition: background 0.2s;
        }
        .alppcalc-close:hover { background: #e6e6ea; }

        .alppcalc-title {
          margin: 0 3rem 0.75rem 0;
          font-size: 1.9rem;
          line-height: 1.1;
          font-weight: 800;
          text-transform: uppercase;
          color: #1a1a1a;
        }
        .alppcalc-desc {
          margin: 0 0 1.5rem;
          font-size: 1.05rem;
          line-height: 1.4;
          color: #444;
        }

        .alppcalc-input-wrap {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border: 2px solid var(--vibe-color-primary);
          border-radius: 12px;
          margin-bottom: 1rem;
        }
        .alppcalc-dollar {
          font-size: 1.4rem;
          font-weight: 700;
          color: #1a1a1a;
        }
        .alppcalc-input {
          flex: 1;
          min-width: 0;
          border: none;
          outline: none;
          font-size: 1.4rem;
          color: #1a1a1a;
          background: transparent;
          padding: 0.5rem 0;
        }
        .alppcalc-input::placeholder { color: #bdbdbd; }

        .alppcalc-calculate {
          width: 100%;
          padding: 1rem;
          border: none;
          border-radius: 12px;
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
          font-size: 1.05rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.02em;
          cursor: pointer;
          transition: opacity 0.15s;
        }
        .alppcalc-calculate:hover { opacity: 0.9; }

        .alppcalc-result {
          margin-top: 1.75rem;
          text-align: center;
          animation: alppcalc-result-in 450ms cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        .alppcalc-amount {
          font-size: clamp(2.75rem, 8vw, 4.25rem);
          font-weight: 800;
          line-height: 1;
          letter-spacing: -0.01em;
          color: #9b9b9b;
        }
        .alppcalc-result-label {
          margin-top: 0.85rem;
          font-size: 1.15rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.01em;
          line-height: 1.25;
          color: #1a1a1a;
        }
        @keyframes alppcalc-result-in {
          from { opacity: 0; transform: translateY(14px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        .alppcalc-note {
          display: flex;
          gap: 0.85rem;
          margin-top: 1.25rem;
          padding: 1rem 1.25rem;
          border-radius: 12px;
          background: #f6f6f8;
        }
        .alppcalc-note-icon {
          flex-shrink: 0;
          color: var(--vibe-color-primary);
          margin-top: 0.1rem;
        }
        .alppcalc-note-text {
          margin: 0;
          font-size: 1rem;
          line-height: 1.45;
          color: #333;
        }

        @media (max-width: 640px) {
          .alppcalc-content { padding: 0.85rem; }
          .alppcalc-panel { padding: 1.5rem; border-radius: 12px; }
          .alppcalc-title { font-size: 1.5rem; margin-right: 2.5rem; }
          .alppcalc-close { top: 0.85rem; right: 0.85rem; }
        }
        @media (prefers-reduced-motion: reduce) {
          .alppcalc-overlay[data-state='open'],
          .alppcalc-overlay[data-state='closed'],
          .alppcalc-content[data-state='open'] .alppcalc-panel,
          .alppcalc-content[data-state='closed'] .alppcalc-panel,
          .alppcalc-result { animation: none; }
        }
      `}</style>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'CheckAffordability',
  displayName: 'Check Affordability Calculator',
  tokens: {
    'checkaffordability.label': {
      label: 'Button Label',
      type: 'text',
      defaultValue: 'Check Affordability',
    },
    'checkaffordability.title': {
      label: 'Calculator Title',
      type: 'text',
      defaultValue: 'Rent Affordability Calculator',
    },
    'checkaffordability.description': {
      label: 'Calculator Description',
      type: 'text',
      defaultValue:
        'Enter your monthly household income before taxes to determine the rent amount you can afford:',
    },
    'checkaffordability.note1': {
      label: 'Note (verifiable income)',
      type: 'text',
      defaultValue:
        'If your income entered above does not qualify you for the apartment you want to rent, please note that we consider all verifiable sources of income.',
    },
    'checkaffordability.note2': {
      label: 'Note (verifiable sources detail)',
      type: 'text',
      defaultValue:
        'This includes but is not limited to: lump sum assets (savings accounts), unemployment, social security or disability benefits, child/spousal support, distributions from an Annuity, 401k, pension, trust fund and student housing. Some verifiable sources may be required to be combined with additional sources to qualify and/or require a guarantor.',
    },
  },
};

(CheckAffordability as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default CheckAffordability;
